import React from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { KibTopLevelAlert } from '@chewy/kib-messaging-react/legacy'
import {
  type BasePuiDialogProps,
  ButtonWithLoader,
  type CustomFieldValidatorState,
  type Patient,
  PuiDialog,
  useFields,
  Validators,
} from '@pbt/pbt-ui-components'

import type {
  Contact,
  PreviousMedicalRecord,
} from '~/api/graphql/generated/types'
import {
  FetchPatientWithPmrDataDocument,
  FetchPatientWithPmrDataOmnichannelDocument,
  useUpdatePatientsWithPreviousMedicalRecordMutation,
  useUpdatePatientsWithPreviousMedicalRecordOmnichannelMutation,
} from '~/api/graphql/generated/types'
import { PriorVetCareStatusName } from '~/constants/previousMedicalRecord'
import {
  getCurrentBusinessId,
  getCurrentBusinessIsOmniChannel,
} from '~/store/reducers/auth'
import { getPriorVetCareStatus } from '~/store/reducers/constants'
import type { TimetableEvent } from '~/types'

import { buildPreviousMedicalRecordInput } from '../buildPreviousMedicalRecordInput'
import PMRConsentStep from '../PMRConsentStep'

import '@chewy/kib-messaging-styles/dist/styles/kib-alert-icon.css'
import '@chewy/kib-messaging-styles/dist/styles/kib-top-level-alert.css'
import styles from './EditPMRDialog.module.scss'

export interface EditPMRDialogProps extends BasePuiDialogProps {
  appointment: TimetableEvent
  clientId: string
  patient: Patient | null
  previousMedicalRecord: PreviousMedicalRecord | null
}

const EditPMRDialog = ({
  open,
  onClose,
  patient,
  clientId,
  appointment,
  previousMedicalRecord: pmr,
}: EditPMRDialogProps) => {
  const { t } = useTranslation(['Common', 'TimeTable', 'Dialogs'])
  const businessId = useSelector(getCurrentBusinessId) ?? ''
  const isOmnichannel = useSelector(getCurrentBusinessIsOmniChannel)
  const priorVetCareStatusConstants = useSelector(getPriorVetCareStatus)

  const priorVetCareYesId = priorVetCareStatusConstants.find(
    (c) => c.name === PriorVetCareStatusName.YES,
  )?.id

  const validatePreviousClinics = ({
    state,
    value,
  }: CustomFieldValidatorState) => {
    const hasSeenVet = state.hasSeenVeterinarian
    const showFindPreviousPractice = hasSeenVet === priorVetCareYesId
    const contacts = (value ?? []) as Contact[]
    return !showFindPreviousPractice || contacts.length > 0
  }

  const validatePetParentConsent = ({
    state,
    value,
  }: CustomFieldValidatorState) => {
    const hasSeenVet = state.hasSeenVeterinarian
    const contacts = (state.previousClinics ?? []) as Contact[]
    const showConsentAndNotesSections =
      contacts.length > 0 && hasSeenVet === priorVetCareYesId
    return !showConsentAndNotesSections || Validators.notEmptyFormField(value)
  }

  const { fields: pmrFields, validate: validatePmrFields } = useFields(
    [
      {
        name: 'hasSeenVeterinarian',
        label: t('TimeTable:PMR_CONSENT.HAS_SEEN_VETERINARIAN_LABEL'),
        initialValue: pmr?.priorVetCareStatus?.id ?? '',
        validators: ['required'],
        messages: {
          required: t('TimeTable:PMR_CONSENT.PLEASE_SELECT_OPTION_ERROR'),
        },
      },
      {
        name: 'previousClinics',
        label: t('TimeTable:PMR_CONSENT.PREVIOUS_PRACTICE_NAME_LABEL'),
        initialValue: pmr?.contacts ?? [],
        validators: [
          {
            validator: validatePreviousClinics,
            validatorName: 'required',
          },
        ],
        messages: {
          required: t('TimeTable:PMR_CONSENT.PLEASE_SELECT_PRACTICE_ERROR'),
        },
      },
      {
        name: 'petParentConsent',
        label: t('TimeTable:PMR_CONSENT.PET_PARENT_CONSENT_LABEL'),
        initialValue: pmr?.consentStatus?.id ?? '',
        validators: [
          {
            validator: validatePetParentConsent,
            validatorName: 'required',
          },
        ],
        messages: {
          required: t('TimeTable:PMR_CONSENT.PLEASE_SELECT_OPTION_ERROR'),
        },
      },
      {
        name: 'internalNote',
        initialValue: pmr?.internalNotes ?? '',
      },
    ],
    false,
  )

  const [updatePatientsWithPreviousMedicalRecordMutation, { loading, error }] =
    (
      isOmnichannel
        ? useUpdatePatientsWithPreviousMedicalRecordOmnichannelMutation
        : useUpdatePatientsWithPreviousMedicalRecordMutation
    )()

  const handleUpdate = async () => {
    if (validatePmrFields()) {
      const pendingPmrSnapshot = {
        hasSeenVeterinarian: pmrFields.hasSeenVeterinarian.value,
        previousClinics: pmrFields.previousClinics.value as Partial<Contact>[],
        petParentConsent: pmrFields.petParentConsent.value,
        internalNote: pmrFields.internalNote.value,
      }

      const previousMedicalRecord = buildPreviousMedicalRecordInput(
        pendingPmrSnapshot,
        businessId,
      )
      const patientId =
        patient?.id ?? appointment?.patientId ?? appointment?.patient ?? ''

      const result = await updatePatientsWithPreviousMedicalRecordMutation({
        variables: {
          businessId,
          clientId:
            clientId ?? appointment?.clientId ?? appointment?.client ?? '',
          input: [
            {
              id: patientId,
              previousMedicalRecord,
            },
          ],
        },
        refetchQueries: [
          {
            query: isOmnichannel
              ? FetchPatientWithPmrDataOmnichannelDocument
              : FetchPatientWithPmrDataDocument,
            variables: { patientId },
          },
        ],
      })

      if (!result.errors) {
        onClose?.()
      }
    }
  }

  const handleCancel = () => {
    onClose?.()
  }

  return (
    <PuiDialog
      aria-labelledby="edit-pmr-dialog"
      classes={{ paper: styles.paper }}
      open={open}
      title={t('TimeTable:PMR_CONSENT.EDIT_PREVIOUS_MEDICAL_RECORDS_TITLE')}
      onClose={onClose}
    >
      <div className={styles.content}>
        <PMRConsentStep
          insideEditPMRDialog
          fields={{
            hasSeenVeterinarian: pmrFields.hasSeenVeterinarian,
            internalNote: pmrFields.internalNote,
            petParentConsent: pmrFields.petParentConsent,
            previousClinics: pmrFields.previousClinics,
          }}
          headerPadding={3}
          patient={patient}
        />
      </div>
      {Boolean(error) && (
        <KibTopLevelAlert
          heading={t('Errors:DEFAULT_ERROR')}
          severity="error"
        />
      )}
      <div className={styles.footer}>
        <ButtonWithLoader
          disabled={Boolean(error)}
          loading={loading}
          onClick={handleUpdate}
        >
          {t('Common:UPDATE_ACTION')}
        </ButtonWithLoader>
        <ButtonWithLoader
          className={styles.cancelButton}
          color="secondary"
          disabled={loading}
          onClick={handleCancel}
        >
          {t('Common:CANCEL_ACTION')}
        </ButtonWithLoader>
      </div>
    </PuiDialog>
  )
}

export default EditPMRDialog
